import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { applyPermissionOverrides, type LoginDto, type UserRole } from '@jalupro/shared';
import { api, tokenStore, onSessionCleared } from '../api';
import type { LoginResult, MeView } from '../api';

/**
 * Mirror the Super Admin's permission-matrix overrides into the shared module so
 * `can()` / navigation / guards evaluate the EFFECTIVE matrix. Best effort: on failure
 * the document defaults stay in force (the API enforces the real matrix anyway).
 */
async function syncPermissions(): Promise<void> {
  try {
    applyPermissionOverrides(await api.permissions.effective());
  } catch {
    applyPermissionOverrides([]);
  }
}

/**
 * Auth state for the whole app: who is signed in, their role, and the helpers
 * the login flow needs (login, logout, refresh `me`).
 *
 * Login is a multi-step challenge (DECISIONS.md: TOTP 2FA is MANDATORY for staff,
 * enrolled on first login):
 *   1. POST /auth/login → may return TOTP_REQUIRED or ENROLLMENT_REQUIRED.
 *   2. Caller resubmits with a totpCode (TOTP_REQUIRED) or runs enrollment
 *      (ENROLLMENT_REQUIRED) and confirms — both end in AUTHENTICATED tokens.
 * The LoginPage owns that UX; this context just exposes `login` returning the
 * raw `LoginResult` plus a `completeLogin` to store tokens once authenticated.
 */

export interface AuthUser extends MeView {}

interface AuthState {
  user: AuthUser | null;
  /** True until the initial `me` probe resolves. */
  initializing: boolean;
  /** Bumped whenever the permission overrides are re-applied, so consumers re-render. */
  permissionsVersion: number;
}

export interface AuthContextValue {
  user: AuthUser | null;
  role: UserRole | null;
  initializing: boolean;
  isAuthenticated: boolean;
  /** Step 1 of login — returns the challenge/result. Does NOT persist tokens by itself. */
  login: (dto: LoginDto) => Promise<LoginResult>;
  /** Persist tokens after an AUTHENTICATED result and load the principal. */
  completeLogin: (tokens: { accessToken: string; refreshToken: string }) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-fetch the current principal (after profile edits, etc.). */
  refresh: () => Promise<void>;
  /** Re-fetch the permission overrides (after the matrix was edited). */
  refreshPermissions: () => Promise<void>;
  /** Changes whenever the effective permission matrix changes. */
  permissionsVersion: number;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, initializing: true, permissionsVersion: 0 });

  const refreshPermissions = useCallback(async () => {
    await syncPermissions();
    setState((s) => ({ ...s, permissionsVersion: s.permissionsVersion + 1 }));
  }, []);

  const loadMe = useCallback(async () => {
    if (!tokenStore.hasSession) {
      setState((s) => ({ ...s, user: null, initializing: false }));
      return;
    }
    try {
      const me = await api.auth.me();
      await syncPermissions();
      setState((s) => ({ user: me, initializing: false, permissionsVersion: s.permissionsVersion + 1 }));
    } catch {
      tokenStore.clear();
      setState((s) => ({ ...s, user: null, initializing: false }));
    }
  }, []);

  // Initial probe + react to a session being cleared by the API client (failed refresh).
  useEffect(() => {
    void loadMe();
    const off = onSessionCleared(() => setState((s) => ({ ...s, user: null, initializing: false })));
    return off;
  }, [loadMe]);

  const login = useCallback((dto: LoginDto) => api.auth.login(dto), []);

  const completeLogin = useCallback(
    async (tokens: { accessToken: string; refreshToken: string }) => {
      tokenStore.set(tokens);
      const me = await api.auth.me();
      await syncPermissions();
      setState((s) => ({ user: me, initializing: false, permissionsVersion: s.permissionsVersion + 1 }));
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Best-effort; clear locally regardless.
    } finally {
      tokenStore.clear();
      applyPermissionOverrides([]);
      setState((s) => ({ ...s, user: null, initializing: false }));
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: state.user,
      role: state.user?.role ?? null,
      initializing: state.initializing,
      isAuthenticated: Boolean(state.user),
      login,
      completeLogin,
      logout,
      refresh: loadMe,
      refreshPermissions,
      permissionsVersion: state.permissionsVersion,
    }),
    [state, login, completeLogin, logout, loadMe, refreshPermissions],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
